import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Custom hook for ContextMenu
export interface ContextMenuState<T> {
    visible: boolean;
    x: number;
    y: number;
    data: T | null;
}

export function useContextMenu<T = unknown>() {
    const [menu, setMenu] = useState<ContextMenuState<T>>({
        visible: false,
        x: 0,
        y: 0,
        data: null,
    });

    const containerRef = useRef<HTMLElement | null>(null);

    const open = useCallback((ev: React.MouseEvent, data?: T) => {
        ev.preventDefault();
        // Position where the user clicked
        setMenu({
            visible: true,
            x: ev.clientX,
            y: ev.clientY,
            data: data ?? null,
        });
    }, []);

    const close = useCallback(() => {
        setMenu((m) => ({ ...m, visible: false }));
    }, []);

    // Close on Escape or outside click
    useEffect(() => {
        if (!menu.visible) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        const onClick = (e: MouseEvent) => {
            // If the click is outside the menu container, close
            if (!containerRef.current?.contains(e.target as Node)) {
                close();
            }
        };

        window.addEventListener('keydown', onKey);
        window.addEventListener('mousedown', onClick);

        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('mousedown', onClick);
        };
    }, [menu.visible, close]);

    // Expose a ref setter to the menu component for outside-click checks
    const setContainerRef = useCallback((el: HTMLElement | null) => {
        containerRef.current = el;
    }, []);

    return useMemo(
        () => ({ menu, open, close, setContainerRef }),
        [menu, open, close, setContainerRef]
    );
}
